// Stack identity shared by the server, the dashboard and the game protocol.
// See docs/protocol.md, section "Stacks".

export type StackType = "item" | "fluid" | "essentia";

export const STACK_TYPES: readonly StackType[] = ["item", "fluid", "essentia"];

export interface Stack {
  type: StackType;
  name: string;
  damage?: number;
  nbt?: string;
  label?: string;
}

/** Builds the protocol key: item:<name>:<damage>[:<nbt>], fluid:<name>, essentia:<name>. */
export function stackKey(stack: Stack): string {
  if (stack.type === "item") {
    const base = `item:${stack.name}:${stack.damage ?? 0}`;
    return stack.nbt ? `${base}:${stack.nbt}` : base;
  }
  return `${stack.type}:${stack.name}`;
}

/**
 * Parses a key back into a stack. Item names contain a colon (mod:item), so an
 * item key is item:<mod>:<item>:<damage>[:<nbt>].
 */
export function parseStackKey(key: string): Stack | null {
  const first = key.indexOf(":");
  if (first < 0) return null;
  const type = key.slice(0, first);
  const rest = key.slice(first + 1);
  if (type === "fluid" || type === "essentia") {
    return rest ? { type, name: rest } : null;
  }
  if (type !== "item") return null;
  const parts = rest.split(":");
  if (parts.length < 3) return null;
  const name = `${parts[0]}:${parts[1]}`;
  const damage = Number(parts[2]);
  if (!Number.isInteger(damage)) return null;
  const nbt = parts.length > 3 ? parts.slice(3).join(":") : undefined;
  return nbt ? { type, name, damage, nbt } : { type, name, damage };
}

/** Validates untrusted input (API body or game message) and returns a clean stack. */
export function normalizeStack(input: unknown): Stack | null {
  if (!input || typeof input !== "object") return null;
  const o = input as Record<string, unknown>;
  if (typeof o.type !== "string" || !STACK_TYPES.includes(o.type as StackType)) return null;
  if (typeof o.name !== "string") return null;
  const type = o.type as StackType;
  let name = o.name.trim();
  if (!name) return null;
  if (type === "essentia") name = name.toLowerCase();
  const stack: Stack = { type, name };
  if (type === "item") {
    if (!name.includes(":")) return null;
    const damage = o.damage === undefined || o.damage === null ? 0 : Number(o.damage);
    if (!Number.isInteger(damage) || damage < 0) return null;
    stack.damage = damage;
    if (typeof o.nbt === "string" && o.nbt) stack.nbt = o.nbt;
  }
  if (typeof o.label === "string" && o.label) stack.label = o.label;
  return stack;
}

/** A readable name for the dashboard when no label is known. */
export function stackDisplayName(stack: Stack): string {
  if (stack.label) return stack.label;
  if (stack.type === "item") return `${stack.name}@${stack.damage ?? 0}`;
  return stack.name;
}
